import { AxiosError } from 'axios';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import {
  useCreateOrderIntent,
  useCreatePurchaseOrder
} from '@/modules/perseo-billing/hooks/use-create-purchase-order';
import { useGiftCardData } from '@/modules/perseo-billing/hooks/use-gift-card-data.ts';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks/use-gtm-billing.tsx';
import {
  delay,
  isCencoPayPayment,
  isKioscoPayment,
  isWebpayPayment
} from '@/modules/perseo-billing/hooks/utils';
import type {
  IPaymentRequest,
  ISinglePayment
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { setIsProcessingPayment } from '@/modules/perseo-billing/store/slices/billing';

export const usePayment = () => {
  const billing = useAppSelector((state) => state.billing);
  const { billingClickPayButtonGtm } = useGtmBillingProduct();
  const { data: cart } = useAppSelector((state) => state.cart);
  const { selectedPaymentMethods, activeAccordion } = billing || {};
  const payments = selectedPaymentMethods?.payments;

  const { mutateAsync: createPurchaseOrder } = useCreatePurchaseOrder();
  const { mutateAsync: createOrderIntent } = useCreateOrderIntent();

  const { paymentButtonGtm } = useGtmBillingProduct();
  const {
    getGiftCardPayment,
    cartHasGiftCardPayment,
    canGiftCardPayTotalAmount
  } = useGiftCardData();
  const dispatch = useAppDispatch();

  const createPayments = (): IPaymentRequest => {
    if (!cart) return {};

    let paymentsOutput: ISinglePayment[] = payments ? [...payments] : [];

    const hasGiftCard = cartHasGiftCardPayment();
    if (hasGiftCard) {
      const canPayTotalAmountWithGC = canGiftCardPayTotalAmount();
      const giftCardPayment = getGiftCardPayment();
      if (canPayTotalAmountWithGC) {
        paymentsOutput = [giftCardPayment];
      }
      if (!canPayTotalAmountWithGC) {
        paymentsOutput = [...paymentsOutput, giftCardPayment];
      }
    }

    if (!paymentsOutput) return {};

    return {
      payments: paymentsOutput
    };
  };

  const handleSubmitForm = async () => {
    let isWebpayPaymentMethod = false;
    try {
      if (!cart) return;

      const payload = createPayments();
      const payments = payload?.payments ?? [];
      const isWebpay = isWebpayPayment(payments);
      isWebpayPaymentMethod = isWebpay;
      const isCencopay = isCencoPayPayment(payments);
      const isKiosco = isKioscoPayment(payments);
      const isOrderIntent = isWebpay || isCencopay || isKiosco;
      const isSavedCard = activeAccordion?.activeAccordionIndex === 1;

      if (!isCencopay && !isWebpay) {
        paymentButtonGtm();
        billingClickPayButtonGtm(payload, isSavedCard);
      }
      dispatch(setIsProcessingPayment(true));

      if (isOrderIntent) {
        await createOrderIntent(payload);
        if (isWebpay) await delay(1000);
        return;
      }

      await createPurchaseOrder(payload);
    } catch (error) {
      const isAxiosError = error instanceof AxiosError;
      if (isAxiosError) return;
      console.error('Payment processing error:', error);
    } finally {
      if (isWebpayPaymentMethod) {
        console.log('Waiting for Webpay to load...');
      } else {
        delay(3000);
        dispatch(setIsProcessingPayment(false));
      }
    }
  };

  return { handleSubmitForm };
};
